import React from 'react';
import { 
  CheckCircle, XCircle, Bookmark, CreditCard, Banknote, 
  MessageCircle, Lock, Clock, Check, X
} from 'lucide-react';

const STICKER_ICONS = {
  available: CheckCircle,
  sold: Check,
  unsold: XCircle,
  booked: Bookmark,
  part_payment_done: CreditCard,
  token_received: Banknote,
  under_negotiation: MessageCircle,
  reserved: Lock,
  coming_soon: Clock
};

const STICKER_STYLES = {
  available: { bg: 'bg-green-500', text: 'text-white', border: 'border-green-600' },
  sold: { bg: 'bg-red-500', text: 'text-white', border: 'border-red-600' },
  unsold: { bg: 'bg-gray-500', text: 'text-white', border: 'border-gray-600' },
  booked: { bg: 'bg-amber-500', text: 'text-white', border: 'border-amber-600' },
  part_payment_done: { bg: 'bg-purple-500', text: 'text-white', border: 'border-purple-600' },
  token_received: { bg: 'bg-blue-500', text: 'text-white', border: 'border-blue-600' },
  under_negotiation: { bg: 'bg-pink-500', text: 'text-white', border: 'border-pink-600' },
  reserved: { bg: 'bg-teal-500', text: 'text-white', border: 'border-teal-600' },
  coming_soon: { bg: 'bg-indigo-500', text: 'text-white', border: 'border-indigo-600' }
};

const STICKER_LABELS = {
  available: 'Available',
  sold: 'SOLD',
  unsold: 'Unsold',
  booked: 'BOOKED',
  part_payment_done: 'Part Payment Done',
  token_received: 'Token Received',
  under_negotiation: 'Under Negotiation',
  reserved: 'Reserved',
  coming_soon: 'Coming Soon'
};

/**
 * StatusSticker - Displays a status badge on product/listing images
 * 
 * Usage:
 * <div className="relative">
 *   <img src="..." />
 *   <StatusSticker type="sold" position="top-left" />
 * </div>
 */
const StatusSticker = ({ 
  type, 
  customText, 
  customColor,
  position = 'top-left', // top-left, top-right, bottom-left, bottom-right, center
  size = 'md', // sm, md, lg
  animated = true,
  showIcon = true,
  diagonal = false // Diagonal ribbon style
}) => {
  if (!type) return null;

  const Icon = STICKER_ICONS[type] || CheckCircle;
  const styles = STICKER_STYLES[type] || STICKER_STYLES.available;
  const label = customText || STICKER_LABELS[type] || type;

  // Position classes
  const positionClasses = {
    'top-left': 'top-2 left-2',
    'top-right': 'top-2 right-2',
    'bottom-left': 'bottom-2 left-2',
    'bottom-right': 'bottom-2 right-2',
    'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
  };

  // Size classes
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  // Custom color override
  const bgColor = customColor ? '' : styles.bg;
  const customStyle = customColor ? { backgroundColor: customColor } : {};

  if (diagonal) {
    // Diagonal ribbon style (like "SOLD" ribbons)
    return (
      <div 
        className={`absolute ${position.includes('right') ? '-right-8 top-4 rotate-45' : '-left-8 top-4 -rotate-45'} 
          ${bgColor} ${styles.text} font-bold uppercase tracking-wider shadow-lg
          ${sizeClasses[size]} w-32 text-center transform origin-center
          ${animated ? 'animate-pulse' : ''}`}
        style={customStyle}
      >
        {label}
      </div>
    );
  }

  return (
    <div 
      className={`absolute ${positionClasses[position]} z-10
        ${bgColor} ${styles.text} ${styles.border}
        ${sizeClasses[size]} rounded-lg font-semibold
        flex items-center gap-1.5 shadow-lg border-2
        ${animated && (type === 'sold' || type === 'booked') ? 'animate-pulse' : ''}
        backdrop-blur-sm`}
      style={customStyle}
    >
      {showIcon && <Icon className={iconSizes[size]} />}
      <span className="uppercase tracking-wide">{label}</span>
    </div>
  );
};

/**
 * StatusStickerSelector - Admin/User dropdown to select sticker type
 */
export const StatusStickerSelector = ({ value, onChange, showCustom = true }) => {
  const [customText, setCustomText] = React.useState('');
  const [customColor, setCustomColor] = React.useState('#EF4444');

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium mb-2">Status Sticker</label>
      
      <div className="grid grid-cols-3 gap-2">
        {Object.entries(STICKER_LABELS).map(([key, label]) => {
          const Icon = STICKER_ICONS[key];
          const styles = STICKER_STYLES[key];
          const isSelected = value === key;

          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange({ status_sticker: key, sticker_text: null, sticker_color: null })}
              className={`p-2 rounded-lg border-2 transition-all flex items-center gap-2 text-sm
                ${isSelected 
                  ? `${styles.bg} ${styles.text} ${styles.border}` 
                  : 'border-white/10 hover:border-white/30'
                }`}
            >
              <Icon className="w-4 h-4" />
              <span className="truncate">{label}</span>
            </button>
          );
        })}
        
        {/* No Sticker Option */}
        <button
          type="button"
          onClick={() => onChange({ status_sticker: null, sticker_text: null, sticker_color: null })}
          className={`p-2 rounded-lg border-2 transition-all flex items-center gap-2 text-sm
            ${!value ? 'border-white/30 bg-white/10' : 'border-white/10 hover:border-white/30'}`}
        >
          <X className="w-4 h-4" />
          <span>No Sticker</span>
        </button>
      </div>

      {showCustom && value && (
        <div className="mt-4 p-4 bg-white/5 rounded-xl space-y-3">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Custom Text (Optional)</label>
            <input
              type="text"
              value={customText}
              onChange={(e) => {
                setCustomText(e.target.value);
                onChange({ status_sticker: value, sticker_text: e.target.value || null, sticker_color: customColor });
              }}
              placeholder={STICKER_LABELS[value]}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Custom Color (Optional)</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={customColor}
                onChange={(e) => {
                  setCustomColor(e.target.value);
                  onChange({ status_sticker: value, sticker_text: customText || null, sticker_color: e.target.value });
                }}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <input
                type="text"
                value={customColor}
                onChange={(e) => {
                  setCustomColor(e.target.value);
                  onChange({ status_sticker: value, sticker_text: customText || null, sticker_color: e.target.value });
                }}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm font-mono"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusSticker;
